/**
 * Admin Onboarding Submissions Review
 * 
 * Admins can:
 * - Review pending submissions
 * - View applicant details and documents
 * - Approve/reject submissions
 * - Generate credentials
 */

import React, { useState, useEffect } from 'react';
import {
  FileCheck, Eye, Check, X, Loader2, Search, Calendar,
  AlertCircle, Download, ChevronDown, Filter, MessageCircle,
  User, Mail, Phone, MapPin, Briefcase, DollarSign, Heart, Landmark
} from 'lucide-react';
import { backendApi } from '../../api/backendApi';
import toast from 'react-hot-toast';

const STATUSES = {
  draft: { label: 'Draft', color: '#999', bgColor: '#f5f5f5' },
  submitted: { label: 'Submitted', color: '#2196f3', bgColor: '#e3f2fd' },
  approved: { label: 'Approved', color: '#4caf50', bgColor: '#e8f5e9' },
  rejected: { label: 'Rejected', color: '#f44336', bgColor: '#ffebee' },
  account_created: { label: 'Account Created', color: '#4caf50', bgColor: '#e8f5e9' },
};

export default function OnboardingSubmissionsPage() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('submitted');
  const [reviewModalOpen, setReviewModalOpen] = useState(false);

  useEffect(() => {
    loadSubmissions();
  }, [statusFilter]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const submissions = await backendApi.onboarding.listSubmissions({ status: statusFilter });
      setSubmissions(submissions);
    } catch (error) {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    if (searchQuery && !sub.full_name.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !sub.email.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  return (
    <div className="ac-page">
      <div style={{ marginBottom: '30px' }}>
        <h1 style={styles.title}>Onboarding Submissions</h1>
        <p style={styles.subtitle}>Review and approve new employee onboarding submissions</p>
      </div>

      {/* Filters */}
      <div style={styles.filtersBar}>
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="">All</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Submissions List */}
      {loading ? (
        <div style={styles.loadingContainer}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : filteredSubmissions.length === 0 ? (
        <div style={styles.emptyState}>
          <FileCheck size={48} style={{ color: '#ccc' }} />
          <h3>No submissions</h3>
          <p>Pending submissions will appear here</p>
        </div>
      ) : (
        <div style={styles.submissionsList}>
          {filteredSubmissions.map((submission) => (
            <SubmissionCard
              key={submission.id}
              submission={submission}
              onReview={() => {
                setSelectedSubmission(submission);
                setReviewModalOpen(true);
              }}
              onRefresh={loadSubmissions}
            />
          ))}
        </div>
      )}

      {/* Review Modal */}
      {reviewModalOpen && selectedSubmission && (
        <ReviewModal
          submission={selectedSubmission}
          onClose={() => {
            setReviewModalOpen(false);
            setSelectedSubmission(null);
          }}
          onRefresh={() => {
            loadSubmissions();
            setReviewModalOpen(false);
          }}
        />
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function SubmissionCard({ submission, onReview }) {
  const status = STATUSES[submission.status] || STATUSES.draft;
  const submittedDate = submission.submitted_at
    ? new Date(submission.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : 'Not submitted';

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div style={styles.cardInfo}>
          <div style={styles.cardTitle}>{submission.full_name}</div>
          <div style={styles.cardMeta}>
            <span>
              <Mail size={14} style={{ marginRight: '4px' }} />
              {submission.email}
            </span>
            <span>
              <Calendar size={14} style={{ marginRight: '4px' }} />
              Submitted {submittedDate}
            </span>
          </div>
        </div>

        <div style={styles.cardRight}>
          <div style={{
            ...styles.statusBadge,
            backgroundColor: status.bgColor,
            color: status.color,
          }}>
            {status.label}
          </div>
          {submission.status === 'submitted' && (
            <button
              onClick={onReview}
              style={styles.reviewButton}
            >
              <Eye size={16} />
              Review
              <ChevronDown size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewModal({ submission, onClose, onRefresh }) {
  const [activeTab, setActiveTab] = useState('info');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  const handleApprove = async () => {
    try {
      setApproving(true);
      await backendApi.onboarding.approveSubmission(submission.id);
      toast.success('Submission approved! Credentials sent to employee');
      onRefresh();
    } catch (error) {
      toast.error(error.message || 'Failed to approve submission');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      setRejecting(true);
      await backendApi.onboarding.rejectSubmission(submission.id, rejectionReason);
      toast.success('Submission rejected. Employee has been notified');
      onRefresh();
    } catch (error) {
      toast.error(error.message || 'Failed to reject submission');
    } finally {
      setRejecting(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Review Submission</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        {/* Tabs */}
        <div style={styles.modalTabs}>
          <button
            onClick={() => setActiveTab('info')}
            style={{
              ...styles.modalTab,
              borderBottom: activeTab === 'info' ? '3px solid #667eea' : '3px solid transparent',
            }}
          >
            <User size={16} />
            Personal Info
          </button>
          <button
            onClick={() => setActiveTab('address')}
            style={{
              ...styles.modalTab,
              borderBottom: activeTab === 'address' ? '3px solid #667eea' : '3px solid transparent',
            }}
          >
            <MapPin size={16} />
            Address
          </button>
          <button
            onClick={() => setActiveTab('employment')}
            style={{
              ...styles.modalTab,
              borderBottom: activeTab === 'employment' ? '3px solid #667eea' : '3px solid transparent',
            }}
          >
            <Briefcase size={16} />
            Employment
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            style={{
              ...styles.modalTab,
              borderBottom: activeTab === 'documents' ? '3px solid #667eea' : '3px solid transparent',
            }}
          >
            <FileCheck size={16} />
            Documents
          </button>
        </div>

        {/* Tab Content */}
        <div style={styles.modalContent}>
          {activeTab === 'info' && (
            <InfoTab submission={submission} />
          )}
          {activeTab === 'address' && (
            <AddressTab submission={submission} />
          )}
          {activeTab === 'employment' && (
            <EmploymentTab submission={submission} />
          )}
          {activeTab === 'documents' && (
            <DocumentsTab submission={submission} />
          )}
        </div>

        {/* Actions */}
        {submission.status === 'submitted' && (
          <div style={styles.modalFooter}>
            {showRejectForm ? (
              <div style={{ flex: 1 }}>
                <textarea
                  placeholder="Reason for rejection (will be sent to employee)..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  style={styles.rejectReasonInput}
                />
                <div style={styles.rejectActions}>
                  <button
                    onClick={() => {
                      setShowRejectForm(false);
                      setRejectionReason('');
                    }}
                    style={styles.cancelButton}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={rejecting}
                    style={styles.rejectButton}
                  >
                    {rejecting ? 'Rejecting...' : 'Confirm Rejection'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setShowRejectForm(true)}
                  style={styles.rejectButtonOutline}
                >
                  <X size={16} />
                  Reject
                </button>
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  style={styles.approveButton}
                >
                  {approving ? (
                    <>
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Approving...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      Approve & Create Account
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        )}

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}

function InfoTab({ submission }) {
  return (
    <div style={styles.tabContent}>
      <InfoRow label="Full Name" value={submission.full_name} icon={<User size={16} />} />
      <InfoRow label="Email" value={submission.email} icon={<Mail size={16} />} />
      <InfoRow label="Phone" value={submission.phone} icon={<Phone size={16} />} />
      <InfoRow label="Date of Birth" value={submission.date_of_birth} />
      <InfoRow label="Gender" value={submission.gender} />
      <InfoRow label="Nationality" value={submission.nationality} />
    </div>
  );
}

function AddressTab({ submission }) {
  return (
    <div style={styles.tabContent}>
      <InfoRow label="Address Line 1" value={submission.address_line1} />
      <InfoRow label="Address Line 2" value={submission.address_line2} />
      <InfoRow label="City" value={submission.city} />
      <InfoRow label="State" value={submission.state} />
      <InfoRow label="Postal Code" value={submission.postal_code} />
      <InfoRow label="Country" value={submission.country} />
    </div>
  );
}

function EmploymentTab({ submission }) {
  return (
    <div style={styles.tabContent}>
      <InfoRow label="Position ID" value={submission.position_id} icon={<Briefcase size={16} />} />
      <InfoRow label="Department ID" value={submission.department_id} />
      <InfoRow label="Location ID" value={submission.location_id} />
      <InfoRow label="Employment Type" value={submission.employment_type} />
      <InfoRow label="Join Date" value={submission.join_date} />
      <InfoRow label="Salary" value={`${submission.salary_currency} ${submission.salary_amount || '-'}`} icon={<DollarSign size={16} />} />
    </div>
  );
}

function DocumentsTab({ submission }) {
  if (!submission.documents || submission.documents.length === 0) {
    return (
      <div style={styles.tabContent}>
        <p style={{ color: '#999', textAlign: 'center', padding: '40px' }}>
          No documents uploaded yet
        </p>
      </div>
    );
  }

  return (
    <div style={styles.tabContent}>
      {submission.documents.map((doc) => (
        <div key={doc.id} style={styles.documentItem}>
          <div style={styles.documentInfo}>
            <FileCheck size={16} style={{ marginRight: '8px', color: '#667eea' }} />
            <div>
              <div style={styles.documentType}>{doc.document_type}</div>
              <div style={styles.documentMeta}>{doc.file_name} • {formatFileSize(doc.file_size)}</div>
            </div>
          </div>
          <a
            href={doc.file_url}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.downloadLink}
          >
            <Download size={16} />
          </a>
        </div>
      ))}
    </div>
  );
}

function InfoRow({ label, value, icon }) {
  return (
    <div style={styles.infoRow}>
      <div style={styles.infoLabel}>
        {icon && <span style={{ marginRight: '6px' }}>{icon}</span>}
        {label}
      </div>
      <div style={styles.infoValue}>{value || '-'}</div>
    </div>
  );
}

function formatFileSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

// ============================================================================
// Styles
// ============================================================================

const styles = {
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    margin: '5px 0 0 0',
  },
  filtersBar: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
  },
  searchInput: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #d0d0d0',
    borderRadius: '6px',
    fontSize: '13px',
  },
  filterSelect: {
    padding: '10px 12px',
    border: '1px solid #d0d0d0',
    borderRadius: '6px',
    fontSize: '13px',
    minWidth: '150px',
  },
  submissionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  card: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px',
    transition: 'all 0.2s',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  cardInfo: {
    flex: 1,
  },
  cardTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 6px 0',
  },
  cardMeta: {
    display: 'flex',
    gap: '16px',
    fontSize: '12px',
    color: '#999',
  },
  cardRight: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
  },
  reviewButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    backgroundColor: '#667eea',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '900px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e0e0e0',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#999',
  },
  modalTabs: {
    display: 'flex',
    borderBottom: '1px solid #e0e0e0',
    padding: '0 24px',
    gap: '20px',
  },
  modalTab: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 0',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    color: '#666',
    whiteSpace: 'nowrap',
  },
  modalContent: {
    flex: 1,
    overflow: 'auto',
    padding: '24px',
  },
  tabContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  infoLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    fontWeight: '500',
    color: '#333',
  },
  infoValue: {
    fontSize: '13px',
    color: '#666',
  },
  documentItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
  },
  documentInfo: {
    display: 'flex',
    gap: '12px',
    flex: 1,
  },
  documentType: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#333',
  },
  documentMeta: {
    fontSize: '12px',
    color: '#999',
  },
  downloadLink: {
    color: '#667eea',
    cursor: 'pointer',
  },
  modalFooter: {
    display: 'flex',
    gap: '12px',
    padding: '20px 24px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#fafafa',
  },
  approveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#4caf50',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
  },
  rejectButtonOutline: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#fff',
    color: '#f44336',
    border: '1px solid #f44336',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
  },
  rejectButton: {
    padding: '10px 20px',
    backgroundColor: '#f44336',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  rejectReasonInput: {
    width: '100%',
    padding: '12px',
    border: '1px solid #d0d0d0',
    borderRadius: '6px',
    fontSize: '13px',
    fontFamily: 'inherit',
    minHeight: '80px',
    resize: 'vertical',
    marginBottom: '12px',
  },
  rejectActions: {
    display: 'flex',
    gap: '12px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '300px',
    color: '#667eea',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#999',
  },
};
